import api from '@/lib/axios';

export type PlaybookStatus = 'draft' | 'in_progress' | 'completed' | 'cancelled';
export type PlaybookStepStatus = 'pending' | 'completed' | 'skipped';

export interface PlaybookProcessType {
  id: string;
  tenantId: string;
  name: string;
  slug: string;
  description?: string | null;
  icon?: string | null;
  color?: string | null;
  sortOrder: number;
  isSystem: boolean;
  isActive: boolean;
}

export interface PlaybookTemplateStep {
  id: string;
  templateId: string;
  title: string;
  contentHtml?: string | null;
  sortOrder: number;
  defaultAssigneeUserIds?: string[];
  alertIntervalDays?: number | null;
  startDaysFromCreation?: number;
  endDaysFromStart?: number;
}

export interface PlaybookTemplate {
  id: string;
  tenantId: string;
  name: string;
  description?: string | null;
  processTypeId?: string | null;
  objectivesHtml?: string | null;
  isActive: boolean;
  steps?: PlaybookTemplateStep[];
}

export interface PlaybookSystemTrigger {
  id: string;
  key: string;
  name: string;
  description?: string | null;
  entityType: string;
  action: string;
  availablePlaceholders: string[];
  sortOrder: number;
}

export interface PlaybookTriggerRule {
  id: string;
  tenantId: string;
  systemTriggerId: string;
  templateId: string;
  name?: string | null;
  isActive: boolean;
  autoStart?: boolean;
  isPrivate?: boolean;
  contractTemplateType?: 'word' | 'd4sign' | null;
  contractTemplateId?: string | null;
  responsibleUserId?: string | null;
  systemTrigger?: PlaybookSystemTrigger;
  template?: { id: string; name: string; isActive: boolean };
}

export interface PlaybookStepAssignee {
  userId: string;
  email?: string | null;
  fullName?: string | null;
  avatarUrl?: string | null;
  name?: string | null;
}

export interface PlaybookStep {
  id: string;
  playbookId: string;
  title: string;
  contentHtml?: string | null;
  sortOrder: number;
  assignees?: PlaybookStepAssignee[];
  assigneeUserIds?: string[];
  alertIntervalDays?: number | null;
  startDate?: string | null;
  endDate?: string | null;
  actualEndDate?: string | null;
  status: PlaybookStepStatus;
  completedAt?: string | null;
  completedBy?: string | null;
  completedByName?: string | null;
  completedByEmail?: string | null;
  completedByAvatarUrl?: string | null;
  skippedAt?: string | null;
  skippedBy?: string | null;
}

export interface PlaybookMember {
  id: string;
  playbookId: string;
  userId: string;
  accessLevel: 'viewer' | 'executor' | 'admin';
  userName?: string;
  userEmail?: string;
}

export interface PlaybookListItem {
  id: string;
  title: string;
  status: PlaybookStatus;
  isPrivate: boolean;
  requireSequentialSteps?: boolean;
  clientId?: string | null;
  clientName?: string | null;
  processTypeId?: string | null;
  creatorName?: string | null;
  createdBy?: string | null;
  stepsTotal: number;
  stepsCompleted: number;
  startedAt?: string | null;
  updatedAt: string;
}

export interface PlaybookMyTaskItem {
  playbookId: string;
  playbookTitle: string;
  playbookStatus?: PlaybookStatus;
  clientName?: string | null;
  requireSequentialSteps: boolean;
  stepId: string;
  stepTitle: string;
  stepContentHtml?: string | null;
  sortOrder: number;
  startDate?: string | null;
  endDate?: string | null;
  completedAt?: string | null;
  isBlockedByOrder: boolean;
}

export interface PlaybookMyCompletedTasksPage {
  data: PlaybookMyTaskItem[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface PlaybookDetail extends PlaybookListItem {
  objectivesHtml?: string | null;
  templateId?: string | null;
  scheduleStartDate?: string | null;
  autoStartOnSchedule?: boolean;
  triggerRuleId?: string | null;
  triggerKey?: string | null;
  triggerContext?: {
    contractId?: string;
    contractName?: string;
    nome_contrato?: string;
    d4signTemplateId?: string;
    wordTemplateId?: string;
  } | null;
  steps: PlaybookStep[];
  members: PlaybookMember[];
  completedAt?: string | null;
  cancelledAt?: string | null;
}

export interface PlaybookHistoryItem {
  id: string;
  playbookId: string;
  stepId?: string | null;
  action: string;
  actorUserId?: string | null;
  actorName?: string | null;
  stepTitle?: string | null;
  metadata?: Record<string, unknown> | null;
  createdAt: string;
}

export interface PlaybookListPage {
  data: PlaybookListItem[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  summary: {
    total: number;
    in_progress: number;
    completed: number;
    draft: number;
    cancelled: number;
  };
}

export interface CreatePlaybookDto {
  title: string;
  objectivesHtml?: string;
  processTypeId?: string;
  clientId?: string;
  templateId?: string;
  scheduleStartDate?: string;
  autoStartOnSchedule?: boolean;
  isPrivate?: boolean;
  requireSequentialSteps?: boolean;
  steps?: Array<{
    id?: string;
    title: string;
    contentHtml?: string;
    sortOrder?: number;
    assigneeUserIds?: string[];
    alertIntervalDays?: number | null;
    startDate?: string | null;
    endDate?: string | null;
  }>;
}

class PlaybooksService {
  async list(params?: {
    status?: string;
    clientId?: string;
    processTypeId?: string;
    mine?: boolean;
    search?: string;
    page?: number;
    pageSize?: number;
  }): Promise<PlaybookListPage> {
    const { data } = await api.get<PlaybookListPage>('/playbooks', { params });
    return data;
  }

  async getMyTasks(): Promise<PlaybookMyTaskItem[]> {
    const { data } = await api.get<PlaybookMyTaskItem[]>('/playbooks/tasks/mine');
    return data;
  }

  async getMyCompletedTasks(
    page = 1,
    pageSize = 5,
  ): Promise<PlaybookMyCompletedTasksPage> {
    const { data } = await api.get<PlaybookMyCompletedTasksPage>(
      '/playbooks/tasks/mine/completed',
      { params: { page, pageSize } },
    );
    return data;
  }

  async getById(id: string): Promise<PlaybookDetail> {
    const { data } = await api.get<PlaybookDetail>(`/playbooks/${id}`);
    return data;
  }

  async create(dto: CreatePlaybookDto): Promise<PlaybookDetail> {
    const { data } = await api.post<PlaybookDetail>('/playbooks', dto);
    return data;
  }

  async update(id: string, dto: Partial<CreatePlaybookDto>): Promise<PlaybookDetail> {
    const { data } = await api.patch<PlaybookDetail>(`/playbooks/${id}`, dto);
    return data;
  }

  async start(id: string): Promise<PlaybookDetail> {
    const { data } = await api.post<PlaybookDetail>(`/playbooks/${id}/start`);
    return data;
  }

  async cancel(id: string): Promise<PlaybookDetail> {
    const { data } = await api.post<PlaybookDetail>(`/playbooks/${id}/cancel`);
    return data;
  }

  async completePlaybook(id: string): Promise<PlaybookDetail> {
    const { data } = await api.post<PlaybookDetail>(`/playbooks/${id}/complete`);
    return data;
  }

  async completeStep(playbookId: string, stepId: string): Promise<PlaybookDetail> {
    const { data } = await api.post<PlaybookDetail>(
      `/playbooks/${playbookId}/steps/${stepId}/complete`,
    );
    return data;
  }

  async skipStep(
    playbookId: string,
    stepId: string,
    reason?: string,
  ): Promise<PlaybookDetail> {
    const { data } = await api.post<PlaybookDetail>(
      `/playbooks/${playbookId}/steps/${stepId}/skip`,
      { reason },
    );
    return data;
  }

  async reopenStep(playbookId: string, stepId: string): Promise<PlaybookDetail> {
    const { data } = await api.post<PlaybookDetail>(
      `/playbooks/${playbookId}/steps/${stepId}/reopen`,
    );
    return data;
  }

  async reorderSteps(playbookId: string, stepIds: string[]): Promise<PlaybookDetail> {
    const { data } = await api.patch<PlaybookDetail>(
      `/playbooks/${playbookId}/steps/reorder`,
      { stepIds },
    );
    return data;
  }

  async getHistory(
    playbookId: string,
    page = 1,
    pageSize = 50,
  ): Promise<PlaybookHistoryItem[]> {
    const { data } = await api.get<PlaybookHistoryItem[]>(
      `/playbooks/${playbookId}/history`,
      { params: { page, pageSize } },
    );
    return data;
  }

  async setMembers(
    playbookId: string,
    members: Array<{ userId: string; accessLevel: string }>,
  ): Promise<PlaybookDetail> {
    const { data } = await api.post<PlaybookDetail>(`/playbooks/${playbookId}/members`, {
      members,
    });
    return data;
  }

  // Process types
  async listProcessTypes(): Promise<PlaybookProcessType[]> {
    const { data } = await api.get<PlaybookProcessType[]>('/playbooks/process-types');
    return data;
  }

  async createProcessType(dto: Partial<PlaybookProcessType>): Promise<PlaybookProcessType> {
    const { data } = await api.post<PlaybookProcessType>('/playbooks/process-types', dto);
    return data;
  }

  async updateProcessType(
    id: string,
    dto: Partial<PlaybookProcessType>,
  ): Promise<PlaybookProcessType> {
    const { data } = await api.patch<PlaybookProcessType>(
      `/playbooks/process-types/${id}`,
      dto,
    );
    return data;
  }

  async deleteProcessType(id: string): Promise<void> {
    await api.delete(`/playbooks/process-types/${id}`);
  }

  // Templates
  async listTemplates(): Promise<PlaybookTemplate[]> {
    const { data } = await api.get<PlaybookTemplate[]>('/playbooks/templates');
    return data;
  }

  async getTemplate(id: string): Promise<PlaybookTemplate> {
    const { data } = await api.get<PlaybookTemplate>(`/playbooks/templates/${id}`);
    return data;
  }

  async createTemplate(dto: Partial<PlaybookTemplate> & { steps?: PlaybookTemplateStep[] }): Promise<PlaybookTemplate> {
    const { data } = await api.post<PlaybookTemplate>('/playbooks/templates', dto);
    return data;
  }

  async updateTemplate(id: string, dto: Partial<PlaybookTemplate>): Promise<PlaybookTemplate> {
    const { data } = await api.patch<PlaybookTemplate>(`/playbooks/templates/${id}`, dto);
    return data;
  }

  async deleteTemplate(id: string): Promise<void> {
    await api.delete(`/playbooks/templates/${id}`);
  }

  async upsertTemplateStep(
    templateId: string,
    step: Partial<PlaybookTemplateStep> & { title: string },
  ): Promise<PlaybookTemplateStep> {
    const { data } = await api.post<PlaybookTemplateStep>(
      `/playbooks/templates/${templateId}/steps`,
      step,
    );
    return data;
  }

  async deleteTemplateStep(templateId: string, stepId: string): Promise<void> {
    await api.delete(`/playbooks/templates/${templateId}/steps/${stepId}`);
  }

  async reorderTemplateSteps(templateId: string, stepIds: string[]): Promise<PlaybookTemplateStep[]> {
    const { data } = await api.patch<PlaybookTemplateStep[]>(
      `/playbooks/templates/${templateId}/steps/reorder`,
      { stepIds },
    );
    return data;
  }

  // Triggers
  async getTriggerCatalog(): Promise<PlaybookSystemTrigger[]> {
    const { data } = await api.get<PlaybookSystemTrigger[]>('/playbooks/triggers/catalog');
    return data;
  }

  async listTriggerRules(): Promise<PlaybookTriggerRule[]> {
    const { data } = await api.get<PlaybookTriggerRule[]>('/playbooks/triggers/rules');
    return data;
  }

  async createTriggerRule(dto: {
    systemTriggerId: string;
    templateId: string;
    name?: string;
    isActive?: boolean;
    autoStart?: boolean;
    isPrivate?: boolean;
    contractTemplateType?: 'word' | 'd4sign';
    contractTemplateId?: string;
    responsibleUserId?: string;
  }): Promise<PlaybookTriggerRule> {
    const { data } = await api.post<PlaybookTriggerRule>('/playbooks/triggers/rules', dto);
    return data;
  }

  async updateTriggerRule(
    id: string,
    dto: {
      systemTriggerId?: string;
      templateId?: string;
      name?: string;
      isActive?: boolean;
      autoStart?: boolean;
      isPrivate?: boolean;
      contractTemplateType?: 'word' | 'd4sign' | null;
      contractTemplateId?: string | null;
      responsibleUserId?: string | null;
    },
  ): Promise<PlaybookTriggerRule> {
    const { data } = await api.patch<PlaybookTriggerRule>(
      `/playbooks/triggers/rules/${id}`,
      dto,
    );
    return data;
  }

  async deleteTriggerRule(id: string): Promise<void> {
    await api.delete(`/playbooks/triggers/rules/${id}`);
  }
}

export const playbooksService = new PlaybooksService();
